/**
 * Overview tiles: followers, pages, campaigns, earnings, views and
 * submissions. Performance owns the full set. Home keeps only the
 * earnings tile beside the profile card.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Money } from "@ui/primitives";
import { formatMoney, fromMinorString, money, type CurrencyCode } from "@contracts/money";
import { fontSize } from "@design/index";
import { api } from "@app/shared/api";
import { Icons } from "@app/shared/icons";
import { SkeletonBone } from "@app/shared/Skeletons";

interface Balance {
  currency: string; pending_minor: string; available_minor: string; total_minor: string;
}

interface PerformanceRow {
  reported_views: string | null; eligible_views: string | null;
}

interface SubmissionRow { id: string; state: string }

interface ParticipationRow { id: string; state: string }

function formatCount(n: number): string {
  return n.toLocaleString("en-GB");
}

const tile: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: 16,
  padding: 20,
};

const metricLabel: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: fontSize.label,
  color: "var(--muted)",
};

export function CreatorEarningsTile({ fill = false }: { fill?: boolean }) {
  const [balances, setBalances] = useState<Balance[] | null>(null);

  useEffect(() => {
    void api.get<{ balances: Balance[] }>("/earnings").then((r) => {
      setBalances(r.ok ? (r.data.balances ?? []) : []);
    });
  }, []);

  const emptyUsd = money(0, "USD");

  return (
    <div style={{
      ...tile,
      display: "grid",
      gap: 10,
      position: "relative",
      flex: fill ? 1 : undefined,
      height: fill ? "100%" : undefined,
      alignContent: "start",
    }}>
      <div className="cm-home-earn-head" style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        gap: 12, flexWrap: "wrap",
      }}>
        <span style={metricLabel}>
          <span style={{
            width: 8, height: 8, borderRadius: 99, background: "var(--accent)",
          }} />
          Total Earnings
        </span>
      </div>

      {balances === null ? (
        <div style={{ display: "grid", gap: 10, paddingTop: 8 }}>
          <SkeletonBone w="48%" h={36} />
          <SkeletonBone w="36%" h={14} />
        </div>
      ) : balances.length === 0 ? (
        <>
          <p style={{
            margin: 0, fontSize: 42, fontWeight: 750, letterSpacing: "-0.04em",
            color: "var(--accent)", fontVariantNumeric: "tabular-nums", lineHeight: 1.05,
          }}>
            {formatMoney(emptyUsd)}
          </p>
          <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
            Pending Earnings <Money value={emptyUsd} size={fontSize.body} />
          </p>
        </>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {balances.map((b) => (
            <div key={b.currency} style={{ display: "grid", gap: 4 }}>
              <p style={{
                margin: 0, fontSize: 36, fontWeight: 750, letterSpacing: "-0.04em",
                color: "var(--accent)", lineHeight: 1.1,
              }}>
                {formatMoney(fromMinorString(b.total_minor, b.currency as CurrencyCode))}
              </p>
              <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                Pending Earnings{" "}
                <Money
                  value={fromMinorString(b.pending_minor, b.currency as CurrencyCode)}
                  size={fontSize.body}
                />
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CreatorStats() {
  const [views, setViews] = useState<number | null>(null);
  const [viewsKnown, setViewsKnown] = useState(false);
  const [submissions, setSubmissions] = useState<SubmissionRow[] | null>(null);
  const [campaignsJoined, setCampaignsJoined] = useState(0);
  const [pages, setPages] = useState(0);

  useEffect(() => {
    void api.get<{ submissions: PerformanceRow[] }>("/performance").then((r) => {
      if (!r.ok) { setViewsKnown(true); setViews(null); return; }
      const rows = r.data.submissions ?? [];
      let sum = 0;
      let any = false;
      for (const row of rows) {
        const raw = row.eligible_views ?? row.reported_views;
        if (raw == null) continue;
        any = true;
        sum += Number(raw);
      }
      setViewsKnown(true);
      setViews(any ? sum : rows.length === 0 ? 0 : null);
    });
    void api.get<{ submissions: SubmissionRow[] }>("/submissions").then((r) => {
      setSubmissions(r.ok ? (r.data.submissions ?? []) : []);
    });
    void api.get<{ participations: ParticipationRow[] }>("/participations").then((r) => {
      setCampaignsJoined(r.ok ? (r.data.participations ?? []).length : 0);
    });
    void api.get<{ connections: { connection_state: string }[] }>("/social-connections").then((r) => {
      const rows = r.ok ? (r.data.connections ?? []) : [];
      setPages(rows.filter((c) =>
        c.connection_state === "CONNECTED"
        || c.connection_state === "EXPIRING"
        || c.connection_state === "INSUFFICIENT_PERMISSION").length);
    });
  }, []);

  const subs = submissions ?? [];

  return (
    <div
      className="cm-home-cols"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)",
        gap: 16,
        alignItems: "stretch",
      }}
    >
      <div
        className="cm-home-metrics"
        style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }}
      >
        {[
          { label: "Followers", value: 0, icon: Icons.social },
          { label: "Pages", value: pages, icon: Icons.submissions },
          { label: "Campaigns", value: campaignsJoined, icon: Icons.work },
        ].map((m) => (
          <div key={m.label} style={{ ...tile, display: "grid", gap: 10 }}>
            <span style={metricLabel}>
              <span style={{ color: "var(--faint)", display: "inline-flex" }}>{m.icon}</span>
              {m.label}
            </span>
            <strong style={{
              fontSize: 36, fontWeight: 750, letterSpacing: "-0.04em",
              fontVariantNumeric: "tabular-nums", lineHeight: 1,
            }}>
              {formatCount(m.value)}
            </strong>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 0 }}>
        <CreatorEarningsTile fill />

        <div className="cm-home-stats" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ ...tile, display: "grid", gap: 10 }}>
            <span style={metricLabel}>
              <span style={{ color: "var(--faint)", display: "inline-flex" }}>{Icons.views}</span>
              Total Views
            </span>
            <strong style={{
              fontSize: 36, fontWeight: 750, letterSpacing: "-0.04em",
              fontVariantNumeric: "tabular-nums", lineHeight: 1,
            }}>
              {!viewsKnown ? "…" : views === null ? "-" : formatCount(views)}
            </strong>
          </div>
          <div style={{ ...tile, display: "grid", gap: 10 }}>
            <span style={metricLabel}>
              <span style={{ color: "var(--faint)", display: "inline-flex" }}>{Icons.submissions}</span>
              Submissions
            </span>
            <strong style={{
              fontSize: 36, fontWeight: 750, letterSpacing: "-0.04em",
              fontVariantNumeric: "tabular-nums", lineHeight: 1,
            }}>
              {submissions === null ? "…" : formatCount(subs.length)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}
